import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import b_ from 'b_';
import { Text, Popup, SimpleText, ThreeDots } from 'platform-components';
import { POPUP_DIRECTIONS } from 'platform-components/constants';
import { withRouterPath, InjectedProps } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import {
    ConfirmationDialog,
    type ConfirmationDialogProps,
} from 'common.components/confirmation-dialog/confirmation-dialog';
import { Student } from 'common.components/typings/class';
import * as userActions from 'common.components/user/actions';
import { getCanEditClass, getIsOnlyTeacherInCurrentClass } from 'common.components/user/selectors';

import * as studentActions from '../actions';
import {
    getCurrentPassportChangeStudentIndex,
    getIsStudentEditing,
    getStudentChangePassportStatus,
} from '../selectors';
import { NEW_STUDENT_ID } from '../students.constants';

import './students__table-student-settings.scss';

interface StoreProps {
    canDeleteStudent: boolean;
    canEditStudent: boolean;
    canPrintStudent: boolean;
    isStudentEditing: boolean;
    shouldWarnBeforeDelete: boolean;
    changePassportLoadingStatus: LOADING_STATUSES;
    currentPassportChangeStudentIndex: number | null;
}

interface DispatchProps {
    resetStudentLoginRequest: typeof studentActions.resetStudentLoginRequest;
    deleteStudentRequest: typeof studentActions.deleteStudentRequest;
    setWizardVariables: typeof userActions.setWizardVariables;
}

interface OwnProps {
    classId: number;
    isArchiveClass: boolean;
    mix?: string;
    onNameChangeRequest(): void;
    onMigratePassportRequest?: () => void;
    student: Student;
    studentsCount: number;
}

interface Props extends OwnProps, StoreProps, DispatchProps, InjectedProps {}

interface State {
    shownConfirmationDialog?: 'resetLogin' | 'deleteStudent' | undefined;
}

const b = b_.with('students');

const COMMON_CONFIRM_DIALOG_PROPS = {
    opened: true,
    closable: true,
    headerType: SimpleText.TYPE.HTML,
    messageType: SimpleText.TYPE.HTML,
    theme: ConfirmationDialog.THEMES.YANDEX,
} satisfies Partial<ConfirmationDialogProps>;

export class ClassTableStudentSettings extends React.Component<Props, State> {
    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };

    state: State = {};

    UNSAFE_componentWillReceiveProps({ studentsCount }: Props) {
        const { studentsCount: prevStudentsCount } = this.props;

        // TODO: передача рефа на попап
        if (studentsCount !== prevStudentsCount && this.popupRef) {
            // TODO К сожалению, Popup внутри platform-components
            //  пока не содержит в типе метод updatePositions, поэтому нужно сделать такой хак
            (this.popupRef as any).updatePositions();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
    }
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    popupRef: Popup | null = null;
    timeout: number | undefined = undefined;

    setPopupRef = (ref: Popup) => {
        this.popupRef = ref;
    };

    handleNameChange = () => {
        this.props.onNameChangeRequest();

        if (this.popupRef) {
            this.popupRef.hide();
        }
    };

    handleDeleteClick = () => {
        if (this.popupRef) {
            this.popupRef.hide();
        }
        this.setState({ shownConfirmationDialog: 'deleteStudent' });
        ym('reachGoal', 'teacher_my_students_student_delete');
    };

    handleMigrateStudentClick = () => {
        const { onMigratePassportRequest } = this.props;

        if (this.popupRef) {
            this.popupRef.hide();
        }

        if (onMigratePassportRequest) {
            onMigratePassportRequest();
        }
    };

    onDeleteStudentApprove = () => {
        const {
            classId,
            deleteStudentRequest,
            student: { id },
        } = this.props;

        deleteStudentRequest(classId, id);
        ym('reachGoal', 'teacher_my_students_student_delete_success');

        this.setState({ shownConfirmationDialog: undefined });
    };

    onDeleteStudentDecline = () => {
        this.setState({ shownConfirmationDialog: undefined });
    };

    handleResetStudentLoginClick = () => {
        if (this.popupRef) {
            this.popupRef.hide();
        }

        this.setState({ shownConfirmationDialog: 'resetLogin' });
    };

    onResetStudentLoginConfirm = () => {
        const {
            classId,
            resetStudentLoginRequest,
            student: { id },
        } = this.props;

        resetStudentLoginRequest(classId, id);
        ym('reachGoal', 'teacher_my_students_student_reset_login_success');

        this.setState({ shownConfirmationDialog: undefined });
    };

    onResetStudentLoginDecline = () => {
        this.setState({ shownConfirmationDialog: undefined });
    };

    handlePrintSettings = () => {
        ym('reachGoal', 'teacher_my_students_student_print');

        const {
            getRouterPath,
            history,
            classId,
            student: { id },
        } = this.props;
        const {
            staticUrls: { labClassPrintStudent },
        } = this.context;

        const path = getRouterPath(labClassPrintStudent, { classId, studentId: id });

        history.push(path);
    };

    // eslint-disable-next-line complexity
    render() {
        const {
            canDeleteStudent,
            canEditStudent,
            isArchiveClass,
            canPrintStudent,
            shouldWarnBeforeDelete,
            student,
            isStudentEditing,
            changePassportLoadingStatus,
            currentPassportChangeStudentIndex,
        } = this.props;
        const { id } = student;

        const { shownConfirmationDialog } = this.state;
        const isNewStudent = id === NEW_STUDENT_ID;

        const shouldShowDotsControl = !isNewStudent && (canDeleteStudent || canEditStudent || canPrintStudent);

        const isPassportLoading =
            changePassportLoadingStatus === LOADING_STATUSES.LOADING && id === currentPassportChangeStudentIndex;

        const deleteConfirmationMessageId = shouldWarnBeforeDelete
            ? 'deleteStudentConfirmation.messageWarn'
            : 'deleteStudentConfirmation.message';

        return (
            <div className={b('table-student-settings')}>
                {shouldShowDotsControl && (
                    <ThreeDots
                        controlMix={b('table-settings')}
                        direction={isArchiveClass ? POPUP_DIRECTIONS.RIGHT : POPUP_DIRECTIONS.LEFT}
                        disabled={isStudentEditing || isPassportLoading}
                        popupProps={{
                            mix: b('table-settings-popup'),
                            ref: this.setPopupRef,
                        }}
                    >
                        {canEditStudent && (
                            <ThreeDots.Item
                                key="student-edit"
                                mix={b('settings-popup-name')}
                                onClick={this.handleNameChange}
                                type={ThreeDots.TYPES.ACTION}
                            >
                                <Text id="class.settings.changeName" weight={Text.WEIGHT.M} />
                            </ThreeDots.Item>
                        )}
                        {!isArchiveClass && (
                            <ThreeDots.Item
                                key="student-print"
                                mix={b('settings-popup-parents')}
                                onClick={this.handlePrintSettings}
                                type={ThreeDots.TYPES.ACTION}
                            >
                                <Text id="class.settings.inviteParents" weight={Text.WEIGHT.M} />
                            </ThreeDots.Item>
                        )}
                        {canEditStudent && (
                            <ThreeDots.Item
                                key="student-reset-login"
                                mix={b('settings-popup-reset-login')}
                                onClick={this.handleResetStudentLoginClick}
                                type={ThreeDots.TYPES.ACTION}
                                withBorder
                            >
                                <Text id="class.settings.resetStudentLogin" weight={Text.WEIGHT.M} />
                            </ThreeDots.Item>
                        )}
                        {canDeleteStudent && (
                            <ThreeDots.Item
                                key="student-delete"
                                mix={b('settings-popup-delete')}
                                onClick={this.handleDeleteClick}
                                type={ThreeDots.TYPES.STRICT_WARNED}
                                withBorder
                            >
                                <Text id="class.settings.deleteStudent" weight={Text.WEIGHT.M} />
                            </ThreeDots.Item>
                        )}
                    </ThreeDots>
                )}
                {shownConfirmationDialog === 'resetLogin' && (
                    <ConfirmationDialog
                        {...COMMON_CONFIRM_DIALOG_PROPS}
                        confirmLabelId="class.settings.resetStudentLoginConfirmation.confirm"
                        headerId="class.settings.resetStudentLoginConfirmation.title"
                        messageId="class.settings.resetStudentLoginConfirmation.message"
                        mix={b('reset-student-login-confirmation-dialog')}
                        onClose={this.onResetStudentLoginDecline}
                        onConfirm={this.onResetStudentLoginConfirm}
                        onReject={this.onResetStudentLoginDecline}
                        rejectLabelId="class.settings.resetStudentLoginConfirmation.cancel"
                    />
                )}
                {shownConfirmationDialog === 'deleteStudent' && (
                    <ConfirmationDialog
                        {...COMMON_CONFIRM_DIALOG_PROPS}
                        confirmLabelId="deleteStudentConfirmation.confirm"
                        headerId="deleteStudentConfirmation.title"
                        messageId={deleteConfirmationMessageId}
                        mix={b('delete-student-confirmation-dialog')}
                        onClose={this.onDeleteStudentDecline}
                        onConfirm={this.onDeleteStudentApprove}
                        onReject={this.onDeleteStudentDecline}
                        rejectLabelId="deleteStudentConfirmation.cancel"
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (storeState: any, { isArchiveClass, classId, student }: OwnProps): StoreProps => {
    const canEdit = getCanEditClass(storeState, classId);
    const isOnlyTeacherInClass = getIsOnlyTeacherInCurrentClass(storeState, { classId });

    return {
        canDeleteStudent: canEdit && !isArchiveClass,
        canEditStudent: canEdit && !isArchiveClass && Boolean(student.no_yandex_account),
        canPrintStudent: !isArchiveClass,
        isStudentEditing: getIsStudentEditing(storeState, { studentId: student?.id }),
        shouldWarnBeforeDelete: !isOnlyTeacherInClass,
        currentPassportChangeStudentIndex: getCurrentPassportChangeStudentIndex(storeState),
        changePassportLoadingStatus: getStudentChangePassportStatus(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    const { resetStudentLoginRequest, deleteStudentRequest } = studentActions;
    const { setWizardVariables } = userActions;

    return bindActionCreators(
        {
            resetStudentLoginRequest,
            deleteStudentRequest,
            setWizardVariables,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(ClassTableStudentSettings));
